/**
 * The Sketch drawing model (pure, unit-tested in skeleton.test.ts): the roof as the installer draws it, in plan.
 *
 * - Vertices (plan points, local metres) and lines between them, each a roof line: `eave` (low edge), `rake`
 *   (sloped outer edge of a gable), `ridge`, `hip`, `valley`.
 * - Faces are the regions the lines enclose (`findFaces`: a half-edge walk of the planar graph; the outer region
 *   and loose lines are left out). Adding a line splits the lines it crosses and snaps its ends onto vertices and
 *   lines (`addLine`), so the graph always stays planar.
 * - Each face has a pitch and the height of its lowest point. Its direction (azimuth, down the slope) is square to
 *   its longest eave, pointing out of the face; a face without an eave keeps the azimuth it was given (a dormer's
 *   cheek, a gambrel's upper face) and takes its height from where it meets the faces already placed
 *   (`resolveSlopes`).
 * - Face ids stay stable while lines are added and removed (`matchFaces`), so a face keeps its settings and its
 *   modules.
 *
 * `roofModel.ts` turns this into the API's `SketchRoof` (what a save sends) and a live roof model for display.
 */
import type { Vec2 } from "../editor/geo.ts";
import type { SkDormer } from "./dormers.ts";

export type EdgeType = "eave" | "rake" | "ridge" | "hip" | "valley";

export interface SkEdge {
  id: string;
  a: string;
  b: string;
  type: EdgeType;
}

export interface FaceSettings {
  pitch: number;
  /** Height of the face's lowest point above the ground, metres. */
  eaveHeight: number;
  /** Down-slope direction for a face without an eave (with one, the eave decides). */
  azimuth: number;
  label?: string | null;
  structureId?: string | null;
  /** Where the pitch came from: the tool's default (`drawn`, also when absent), the installer (`set`: Assist
   * helpers never replace it) or an accepted roof hint (`hint`). */
  pitchSource?: "drawn" | "set" | "hint" | null;
}

export interface SkFace extends FaceSettings {
  id: string;
  /** Vertex ids, counter-clockwise seen from above. */
  boundary: string[];
}

export type StructureKind = "house" | "garage" | "shed" | "carport" | "other";

/** A building of the Sketch (phase 4, structures.ts): the house (`main`), a garage, a shed… Faces name it as
 * `structureId`, like the automatic pipeline's structures. `footprint`: the outline its roof was made from (the
 * straight skeleton, footprint.ts) with each side's mode and pitch, kept so a side can flip gable ↔ hip. */
export interface SkStructure {
  id: string;
  name: string;
  kind: StructureKind;
  eaveHeight: number;
  hidden?: boolean;
  footprint?: { ring: Vec2[]; edges: { mode: "hip" | "gable"; pitch: number }[] } | null;
}

export interface Sketch {
  vertices: Record<string, Vec2>;
  edges: SkEdge[];
  faces: SkFace[];
  /** Phase 4: the buildings (absent: one, the house) and the dormers (dormers.ts). */
  structures?: SkStructure[];
  dormers?: SkDormer[];
}

export const EMPTY_SKETCH: Sketch = { vertices: {}, edges: [], faces: [] };
const RAD = Math.PI / 180;
export const SNAP_M = 0.25;
const EPS = 1e-9;

// ----------------------------------------------------------------------------------------------- ids
export function nextId(prefix: string, used: Iterable<string>): string {
  const taken = new Set(used);
  let n = 1;
  while (taken.has(`${prefix}${n}`)) n++;
  return `${prefix}${n}`;
}

// ----------------------------------------------------------------------------------------- geometry
export function ringArea(points: Vec2[]): number {
  let a = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const q = points[(i + 1) % points.length]!;
    a += p[0] * q[1] - q[0] * p[1];
  }
  return a / 2;
}

export function centroid(points: Vec2[]): Vec2 {
  const a = ringArea(points);
  if (Math.abs(a) < 1e-12) return [points.reduce((s, p) => s + p[0], 0) / points.length, points.reduce((s, p) => s + p[1], 0) / points.length];
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const q = points[(i + 1) % points.length]!;
    const k = p[0] * q[1] - q[0] * p[1];
    cx += (p[0] + q[0]) * k;
    cy += (p[1] + q[1]) * k;
  }
  return [cx / (6 * a), cy / (6 * a)];
}

export function pointInRing([x, y]: Vec2, ring: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** The closest point of segment ab to p, and its parameter t in [0, 1]. */
export function closestOnSegment(p: Vec2, a: Vec2, b: Vec2): { point: Vec2; t: number; distance: number } {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2)) : 0;
  const point: Vec2 = [a[0] + t * dx, a[1] + t * dy];
  return { point, t, distance: Math.hypot(p[0] - point[0], p[1] - point[1]) };
}

/** Where segments ab and cd cross strictly inside both (parameters along each), or null. */
export function crossing(a: Vec2, b: Vec2, c: Vec2, d: Vec2): { point: Vec2; t: number; u: number } | null {
  const r: Vec2 = [b[0] - a[0], b[1] - a[1]];
  const s: Vec2 = [d[0] - c[0], d[1] - c[1]];
  const den = r[0] * s[1] - r[1] * s[0];
  if (Math.abs(den) < EPS) return null;
  const t = ((c[0] - a[0]) * s[1] - (c[1] - a[1]) * s[0]) / den;
  const u = ((c[0] - a[0]) * r[1] - (c[1] - a[1]) * r[0]) / den;
  if (t <= 1e-6 || t >= 1 - 1e-6 || u <= 1e-6 || u >= 1 - 1e-6) return null;
  return { point: [a[0] + t * r[0], a[1] + t * r[1]], t, u };
}

// ------------------------------------------------------------------------------------------- faces
/** Planar faces of the line graph, counter-clockwise; the outer region and loose lines are left out. */
export function findFaces(vertices: Record<string, Vec2>, edges: SkEdge[]): string[][] {
  // prune loose ends (vertices of degree 1) until none is left: they enclose nothing
  let live = edges.filter((e) => e.a !== e.b && vertices[e.a] && vertices[e.b]);
  for (;;) {
    const degree = new Map<string, number>();
    for (const e of live) {
      degree.set(e.a, (degree.get(e.a) ?? 0) + 1);
      degree.set(e.b, (degree.get(e.b) ?? 0) + 1);
    }
    const next = live.filter((e) => (degree.get(e.a) ?? 0) > 1 && (degree.get(e.b) ?? 0) > 1);
    if (next.length === live.length) break;
    live = next;
  }
  const out = new Map<string, string[]>();
  const add = (a: string, b: string) => {
    const list = out.get(a) ?? [];
    if (!list.includes(b)) list.push(b);
    out.set(a, list);
  };
  for (const e of live) {
    add(e.a, e.b);
    add(e.b, e.a);
  }
  const angle = (from: string, to: string) => Math.atan2(vertices[to]![1] - vertices[from]![1], vertices[to]![0] - vertices[from]![0]);
  for (const [v, list] of out) list.sort((p, q) => angle(v, p) - angle(v, q));
  const visited = new Set<string>();
  const faces: string[][] = [];
  for (const [start, list] of out) {
    for (const first of list) {
      if (visited.has(`${start}>${first}`)) continue;
      const ring: string[] = [];
      let u = start;
      let v = first;
      let guard = 0;
      while (!visited.has(`${u}>${v}`) && guard++ < 10000) {
        visited.add(`${u}>${v}`);
        ring.push(u);
        // at v, turn to the neighbour just clockwise of the way back (u): the face stays on the left
        const around = out.get(v)!;
        const back = around.indexOf(u);
        const w = around[(back - 1 + around.length) % around.length]!;
        u = v;
        v = w;
      }
      const points = ring.map((id) => vertices[id]!);
      if (ring.length >= 3 && new Set(ring).size === ring.length && ringArea(points) > 1e-6) faces.push(ring);
    }
  }
  return faces;
}

const cycleKey = (ring: string[]) => {
  const i = ring.indexOf([...ring].sort()[0]!);
  return [...ring.slice(i), ...ring.slice(0, i)].join(",");
};

/**
 * Faces of the new line graph with the ids and settings of the faces they come from: the same outline keeps its
 * face; a face split in two keeps its id on the larger part (the other part copies its settings); new regions take
 * `defaults`.
 */
export function matchFaces(vertices: Record<string, Vec2>, rings: string[][], previous: SkFace[], defaults: FaceSettings): SkFace[] {
  const byKey = new Map(previous.map((f) => [cycleKey(f.boundary), f]));
  const taken = new Set<string>();
  const result: (SkFace | null)[] = rings.map(() => null);
  rings.forEach((ring, i) => {
    const same = byKey.get(cycleKey(ring));
    if (same && !taken.has(same.id)) {
      result[i] = { ...same, boundary: ring };
      taken.add(same.id);
    }
  });
  const order = rings.map((ring, i) => ({ i, area: ringArea(ring.map((v) => vertices[v]!)) })).sort((p, q) => q.area - p.area);
  for (const { i } of order) {
    if (result[i]) continue;
    const ring = rings[i]!;
    const c = centroid(ring.map((v) => vertices[v]!));
    const parent = previous.find((f) => f.boundary.every((v) => vertices[v]) && pointInRing(c, f.boundary.map((v) => vertices[v]!)));
    const used = [...previous.map((f) => f.id), ...result.filter(Boolean).map((f) => f!.id)];
    if (parent && !taken.has(parent.id)) {
      result[i] = { ...parent, boundary: ring };
      taken.add(parent.id);
    } else {
      const settings = parent ? { pitch: parent.pitch, eaveHeight: parent.eaveHeight, azimuth: parent.azimuth, structureId: parent.structureId } : defaults;
      const id = nextId("f", used);
      result[i] = { ...settings, label: null, id, boundary: ring };
    }
  }
  return result as SkFace[];
}

// ----------------------------------------------------------------------------------------- editing
export interface Snap {
  /** An existing vertex, or a point on a line (which `addLine` splits there). */
  vertex?: string;
  edge?: string;
  point: Vec2;
}

/** What a pointer at `p` snaps to: a vertex within `tol`, else a line within `tol`, else nothing. */
/** The direction (degrees from east) of the sketch's longest line, the reference new lines snap square to. */
export function mainDirection(s: Sketch): number {
  let best = 0;
  let angle = 0;
  for (const e of s.edges) {
    const a = s.vertices[e.a];
    const b = s.vertices[e.b];
    if (!a || !b) continue;
    const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (length > best) {
      best = length;
      angle = Math.atan2(b[1] - a[1], b[0] - a[0]) / RAD;
    }
  }
  return angle;
}

export function snapPoint(sketch: Sketch, p: Vec2, tol = SNAP_M): Snap {
  let best: Snap = { point: p };
  let bestD = tol;
  for (const [id, v] of Object.entries(sketch.vertices)) {
    const d = Math.hypot(v[0] - p[0], v[1] - p[1]);
    if (d <= bestD) {
      bestD = d;
      best = { vertex: id, point: v };
    }
  }
  if (best.vertex) return best;
  for (const e of sketch.edges) {
    const hit = closestOnSegment(p, sketch.vertices[e.a]!, sketch.vertices[e.b]!);
    if (hit.distance <= bestD && hit.t > 0.01 && hit.t < 0.99) {
      bestD = hit.distance;
      best = { edge: e.id, point: hit.point };
    }
  }
  return best;
}

/** Snaps a direction from `from` to the nearest multiple of `stepDeg` from `refDeg` (the previous line's
 * direction: corners square to it on a turned house; 0 = east), within `toleranceDeg`. */
export function snapDirection(from: Vec2, to: Vec2, stepDeg = 45, toleranceDeg = 6, refDeg = 0): Vec2 {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const length = Math.hypot(dx, dy);
  if (length < EPS) return to;
  const angle = Math.atan2(dy, dx) / RAD;
  const snapped = refDeg + Math.round((angle - refDeg) / stepDeg) * stepDeg;
  if (Math.abs(angle - snapped) > toleranceDeg) return to;
  return [from[0] + length * Math.cos(snapped * RAD), from[1] + length * Math.sin(snapped * RAD)];
}

function splitEdge(s: Sketch, edgeId: string, point: Vec2): { sketch: Sketch; vertex: string } {
  const edge = s.edges.find((e) => e.id === edgeId)!;
  const vertex = nextId("v", Object.keys(s.vertices));
  const vertices = { ...s.vertices, [vertex]: point };
  const id2 = nextId("e", s.edges.map((e) => e.id));
  const edges = s.edges.flatMap((e) => (e.id === edgeId ? [{ ...e, b: vertex }, { id: id2, a: vertex, b: edge.b, type: e.type }] : [e]));
  // faces through the split line get the new vertex in their outline
  const faces = s.faces.map((f) => {
    const n = f.boundary.length;
    for (let i = 0; i < n; i++) {
      const a = f.boundary[i]!;
      const b = f.boundary[(i + 1) % n]!;
      if ((a === edge.a && b === edge.b) || (a === edge.b && b === edge.a)) {
        return { ...f, boundary: [...f.boundary.slice(0, i + 1), vertex, ...f.boundary.slice(i + 1)] };
      }
    }
    return f;
  });
  return { sketch: { ...s, vertices, edges, faces }, vertex };
}

function vertexFor(s: Sketch, snap: Snap): { sketch: Sketch; vertex: string } {
  if (snap.vertex) return { sketch: s, vertex: snap.vertex };
  if (snap.edge) return splitEdge(s, snap.edge, snap.point);
  const vertex = nextId("v", Object.keys(s.vertices));
  return { sketch: { ...s, vertices: { ...s.vertices, [vertex]: snap.point } }, vertex };
}

/**
 * The sketch with a new line from `from` to `to` (snapped onto vertices and lines): lines it crosses are split
 * there, so it is cut into segments between crossings; then faces are found again (`defaults` for new ones).
 */
export function addLine(s: Sketch, from: Snap, to: Snap, type: EdgeType, defaults: FaceSettings): Sketch {
  let current = s;
  const start = vertexFor(current, from);
  current = start.sketch;
  const end = vertexFor(current, to);
  current = end.sketch;
  if (start.vertex === end.vertex) return s;
  // split every line the new one crosses, and chain the pieces
  const a = current.vertices[start.vertex]!;
  const b = current.vertices[end.vertex]!;
  const cuts: { t: number; vertex: string }[] = [];
  for (const e of [...current.edges]) {
    if ([e.a, e.b].some((v) => v === start.vertex || v === end.vertex)) continue;
    const hit = crossing(a, b, current.vertices[e.a]!, current.vertices[e.b]!);
    if (!hit) continue;
    const split = splitEdge(current, e.id, hit.point);
    current = split.sketch;
    cuts.push({ t: hit.t, vertex: split.vertex });
  }
  // vertices lying on the new line become cut points too
  for (const [id, v] of Object.entries(current.vertices)) {
    if (id === start.vertex || id === end.vertex || cuts.some((c) => c.vertex === id)) continue;
    const hit = closestOnSegment(v, a, b);
    if (hit.distance < 1e-3 && hit.t > 1e-6 && hit.t < 1 - 1e-6) cuts.push({ t: hit.t, vertex: id });
  }
  const chain = [start.vertex, ...cuts.sort((p, q) => p.t - q.t).map((c) => c.vertex), end.vertex];
  const edges = [...current.edges];
  for (let i = 0; i + 1 < chain.length; i++) {
    const [p, q] = [chain[i]!, chain[i + 1]!];
    if (edges.some((e) => (e.a === p && e.b === q) || (e.a === q && e.b === p))) continue;
    edges.push({ id: nextId("e", edges.map((e) => e.id)), a: p, b: q, type });
  }
  return refaced({ ...current, edges }, defaults);
}

/** The faces found again after the lines changed, matched to the previous ones. */
export function refaced(s: Sketch, defaults: FaceSettings): Sketch {
  return { ...s, faces: matchFaces(s.vertices, findFaces(s.vertices, s.edges), s.faces, defaults) };
}

/** Without a line (and the vertices nothing uses any more). */
export function removeEdge(s: Sketch, edgeId: string, defaults: FaceSettings): Sketch {
  const edges = s.edges.filter((e) => e.id !== edgeId);
  const used = new Set(edges.flatMap((e) => [e.a, e.b]));
  const vertices = Object.fromEntries(Object.entries(s.vertices).filter(([id]) => used.has(id)));
  return refaced({ ...s, vertices, edges, faces: s.faces }, defaults);
}

export function moveVertex(s: Sketch, id: string, to: Vec2): Sketch {
  return { ...s, vertices: { ...s.vertices, [id]: to } };
}

export function setEdgeType(s: Sketch, edgeId: string, type: EdgeType): Sketch {
  return { ...s, edges: s.edges.map((e) => (e.id === edgeId ? { ...e, type } : e)) };
}

export function updateFace(s: Sketch, faceId: string, change: Partial<FaceSettings>): Sketch {
  return { ...s, faces: s.faces.map((f) => (f.id === faceId ? { ...f, ...change } : f)) };
}

// ----------------------------------------------------------------------------------------- slopes
export interface Slope {
  pitch: number;
  azimuth: number;
  eaveHeight: number;
  /** The eave line the direction comes from (null: the face's own azimuth). */
  eave: string | null;
}

/** The down-slope direction square to a face's side a→b (counter-clockwise ring: out of the face). */
export function outwardAzimuth(a: Vec2, b: Vec2): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  return ((Math.atan2(dy, -dx) / RAD) % 360 + 360) % 360;
}

function sideEdge(s: Sketch, p: string, q: string): SkEdge | undefined {
  return s.edges.find((e) => (e.a === p && e.b === q) || (e.a === q && e.b === p));
}

/** The longest eave on a face's outline (side a→b in ring order), or null. */
export function faceEave(s: Sketch, face: SkFace): { edge: SkEdge; a: string; b: string } | null {
  let best: { edge: SkEdge; a: string; b: string; length: number } | null = null;
  const n = face.boundary.length;
  for (let i = 0; i < n; i++) {
    const a = face.boundary[i]!;
    const b = face.boundary[(i + 1) % n]!;
    const edge = sideEdge(s, a, b);
    if (!edge || edge.type !== "eave") continue;
    const length = Math.hypot(s.vertices[b]![0] - s.vertices[a]![0], s.vertices[b]![1] - s.vertices[a]![1]);
    if (!best || length > best.length) best = { edge, a, b, length };
  }
  return best;
}

/** The anchor of a plane (as the server, sketch/roof.py): the vertex lowest along the slope; on a level eave
 * the one furthest back along u (so uv coordinates on the face are positive). */
export function anchorIndex(points: Vec2[], azimuthDeg: number): number {
  const az = azimuthDeg * RAD;
  const down: Vec2 = [Math.sin(az), Math.cos(az)];
  const along: Vec2 = [-Math.cos(az), Math.sin(az)];
  const low = points.map((p) => p[0] * down[0] + p[1] * down[1]);
  const max = Math.max(...low);
  let best = -1;
  for (let i = 0; i < points.length; i++) {
    if (low[i]! < max - 1e-3) continue;
    if (best < 0 || points[i]![0] * along[0] + points[i]![1] * along[1] < points[best]![0] * along[0] + points[best]![1] * along[1]) best = i;
  }
  return best;
}

/** Height of the plane (pitch, azimuth, through `anchor` at `z`) at a plan point. */
export function planeZ(pitch: number, azimuth: number, anchor: Vec2, z: number, p: Vec2): number {
  const az = azimuth * RAD;
  const down = (p[0] - anchor[0]) * Math.sin(az) + (p[1] - anchor[1]) * Math.cos(az);
  return z - down * Math.tan(pitch * RAD);
}

/**
 * Every face's pitch, azimuth and eave height. A face with an eave slopes square to it from its own eave height.
 * A face without one keeps its azimuth and sits where it meets placed faces: its anchor vertex's height on them
 * (passes over the faces until nothing changes); one meeting none keeps its own eave height.
 */
export function resolveSlopes(s: Sketch): Record<string, Slope> {
  const slopes: Record<string, Slope> = {};
  const placed = new Set<string>();
  for (const f of s.faces) {
    const eave = faceEave(s, f);
    if (eave) {
      slopes[f.id] = { pitch: f.pitch, azimuth: outwardAzimuth(s.vertices[eave.a]!, s.vertices[eave.b]!), eaveHeight: f.eaveHeight, eave: eave.edge.id };
      placed.add(f.id);
    } else {
      slopes[f.id] = { pitch: f.pitch, azimuth: ((f.azimuth % 360) + 360) % 360, eaveHeight: f.eaveHeight, eave: null };
    }
  }
  const heightOn = (faceId: string, p: Vec2): number => {
    const f = s.faces.find((x) => x.id === faceId)!;
    const slope = slopes[faceId]!;
    const pts = f.boundary.map((v) => s.vertices[v]!);
    return planeZ(slope.pitch, slope.azimuth, pts[anchorIndex(pts, slope.azimuth)]!, slope.eaveHeight, p);
  };
  for (let pass = 0; pass < s.faces.length; pass++) {
    let changed = false;
    for (const f of s.faces) {
      if (placed.has(f.id)) continue;
      const slope = slopes[f.id]!;
      const pts = f.boundary.map((v) => s.vertices[v]!);
      const anchor = f.boundary[anchorIndex(pts, slope.azimuth)]!;
      const neighbours = s.faces.filter((g) => g.id !== f.id && placed.has(g.id) && g.boundary.includes(anchor));
      if (!neighbours.length) continue;
      const z = neighbours.reduce((sum, g) => sum + heightOn(g.id, s.vertices[anchor]!), 0) / neighbours.length;
      slopes[f.id] = { ...slope, eaveHeight: Math.max(0, z) };
      placed.add(f.id);
      changed = true;
    }
    if (!changed) break;
  }
  return slopes;
}
